/* ==================================================================
   HyrSkill landing content
   ------------------------------------------------------------------
   ALL copy and data for the landing page lives here so the UI
   components stay presentational. Anything in this file can later be
   swapped for an API response without touching a single component.

   Placeholder data is flagged explicitly (`placeholder: true` /
   PLACEHOLDER_* naming) so it is obvious what still needs replacing
   with real platform data.
   ================================================================== */

/* ---------------- Routes the CTAs point at ---------------- */
export const ROUTES = {
  studentSignup: '/student/signup',
  recruiterSignup: '/recruiter/signup',
  login: '/login',
  pricing: '/pricing',
};

/* ---------------- Navigation ---------------- */
export const NAV_LINKS = [
  { label: 'Opportunities', href: '#opportunities' },
  { label: 'For Students', href: '#students' },
  { label: 'For Companies', href: '#companies' },
  { label: 'About', href: '#about' },
];

/* ---------------- Hero ---------------- */
export const HERO = {
  eyebrow: 'Internships and early-career roles',
  /* Rendered word-by-word; `accent` paints the line in brand blue. */
  headlineLines: [
    { words: ['Great', 'talent.'], accent: false },
    { words: ['Great', 'opportunities.'], accent: true },
  ],
  sub: 'HyrSkill connects ambitious students with startups and companies hiring for internships and early-career roles.',
  primaryCta: { label: 'Explore Opportunities', to: ROUTES.studentSignup },
  secondaryCta: { label: 'Hire Talent', to: ROUTES.recruiterSignup },
};

/* Floating cards in the hero ecosystem.
   x / y are percentages inside the stage; depth drives parallax strength.
   priority 3 cards are dropped on small screens to keep motion calm. */
export const HERO_CARDS = [
  { id: 'ai', role: 'AI Engineer Intern', meta: 'Remote', x: -6, y: 2, depth: 1.15, tone: 'blue', priority: 1 },
  { id: 'product', role: 'Product Intern', meta: 'Bangalore', x: 58, y: -6, depth: 0.75, tone: 'plain', priority: 2 },
  { id: 'swe', role: 'Software Engineer Intern', meta: 'Remote', x: 58, y: 72, depth: 1.35, tone: 'blue', priority: 1 },
  { id: 'data', role: 'Data Analyst Intern', meta: 'Mumbai', x: -14, y: 38, depth: 0.9, tone: 'plain', priority: 2 },
  { id: 'marketing', role: 'Marketing Intern', meta: 'Delhi NCR', x: 20, y: 84, depth: 0.6, tone: 'plain', priority: 3 },
];

/* ---------------- Scroll story ---------------- */
export const STORY_SKILLS = ['Python', 'React', 'AI/ML', 'Product', 'Data', 'Design', 'Marketing'];

export const STORY = {
  chapters: [
    {
      id: 'skills',
      index: '01',
      title: 'Students have the skills.',
      body: 'Built through coursework, side projects, hackathons and internships.',
    },
    {
      id: 'roles',
      index: '02',
      title: 'Startups have the opportunities.',
      body: 'Real internships and early-career roles that teams need filled now.',
    },
    {
      id: 'connect',
      index: '03',
      title: 'HyrSkill connects them.',
      body: 'One place where the right student meets the right opening.',
    },
  ],
  roles: [
    { role: 'AI Engineer Intern', company: 'Nexora Labs' },
    { role: 'Product Intern', company: 'Fold' },
    { role: 'Software Engineer Intern', company: 'Zenspark' },
    { role: 'Data Analyst Intern', company: 'Northwind' },
  ],
  people: [
    { name: 'Aryan', track: 'AI / ML' },
    { name: 'Priya', track: 'Product' },
    { name: 'Rahul', track: 'Software' },
  ],
  openings: [
    { role: 'AI Engineer Intern', company: 'Nexora Labs' },
    { role: 'Product Intern', company: 'Fold' },
    { role: 'Software Engineer Intern', company: 'Zenspark' },
  ],
};

/* ---------------- How HyrSkill works ---------------- */
export const STEPS = [
  { index: '01', title: 'Discover', body: 'Students discover relevant internships and opportunities.', side: 'student' },
  { index: '02', title: 'Apply', body: 'Students apply directly through HyrSkill.', side: 'student' },
  { index: '03', title: 'Connect', body: 'Startups discover high-quality student talent.', side: 'company' },
  { index: '04', title: 'Hire', body: 'Companies find the right candidates faster.', side: 'company' },
];

/* ---------------- Opportunities board ----------------
   PLACEHOLDER LISTINGS used for the board preview. Replace with a real
   feed (e.g. a public GET /api/jobs) when one exists — the shape below
   is exactly what <JobCard /> consumes. */
export const PLACEHOLDER_JOBS = [
  {
    id: 'job-ai',
    role: 'AI Engineer Intern',
    company: 'Nexora Labs',
    companyTone: 'blue',
    location: 'Remote',
    mode: 'Remote',
    stipend: '₹20K – ₹40K/month',
    duration: '6 months',
    skills: ['Python', 'LangChain', 'PyTorch'],
    placeholder: true,
  },
  {
    id: 'job-product',
    role: 'Product Intern',
    company: 'Fold',
    companyTone: 'violet',
    location: 'Bangalore',
    mode: 'Onsite',
    stipend: '₹15K – ₹30K/month',
    duration: '3 months',
    skills: ['Analytics', 'Figma', 'Research'],
    placeholder: true,
  },
  {
    id: 'job-swe',
    role: 'Software Engineer Intern',
    company: 'Zenspark',
    companyTone: 'teal',
    location: 'Remote',
    mode: 'Remote',
    stipend: '₹20K – ₹50K/month',
    duration: '6 months',
    skills: ['React', 'Node.js', 'PostgreSQL'],
    placeholder: true,
  },
  {
    id: 'job-data',
    role: 'Data Analyst Intern',
    company: 'Northwind',
    companyTone: 'amber',
    location: 'Mumbai',
    mode: 'Hybrid',
    stipend: '₹15K – ₹25K/month',
    duration: '4 months',
    skills: ['SQL', 'Python', 'Dashboards'],
    placeholder: true,
  },
];

export const JOB_FILTERS = [
  { id: 'all', label: 'All roles' },
  { id: 'Remote', label: 'Remote' },
  { id: 'Onsite', label: 'Onsite' },
  { id: 'Hybrid', label: 'Hybrid' },
];

/* ---------------- Student section ---------------- */
export const STUDENT_SECTION = {
  eyebrow: 'For students',
  heading: 'Your next opportunity is closer than you think.',
  sub: 'Find internships built around your skills, interests and ambitions.',
  cta: { label: 'Find an Internship', to: ROUTES.studentSignup },
  /* Steps that travel along the path toward the Opportunity card */
  journey: [
    { id: 'resume', label: 'Resume' },
    { id: 'skills', label: 'Skills' },
    { id: 'projects', label: 'Projects' },
    { id: 'applications', label: 'Applications' },
    { id: 'interview', label: 'Interview' },
    { id: 'offer', label: 'Offer' },
  ],
  destination: { title: 'Opportunity', sub: 'Matched to you' },
};

/* ---------------- Company section ---------------- */
export const COMPANY_SECTION = {
  eyebrow: 'For startups and companies',
  heading: 'Hiring great students shouldn’t take weeks.',
  sub: 'Find skilled students, discover relevant profiles and start hiring faster.',
  cta: { label: 'Hire Students', to: ROUTES.recruiterSignup },
};

/* PLACEHOLDER PROFILES - illustrative candidate cards, not real students. */
export const PLACEHOLDER_CANDIDATES = [
  {
    id: 'cand-aryan',
    name: 'Aryan',
    track: 'AI / ML',
    skills: ['Python', 'LangChain', 'SQL'],
    tone: 'blue',
    signals: ['Open to remote', 'Available from Aug'],
    placeholder: true,
  },
  {
    id: 'cand-priya',
    name: 'Priya',
    track: 'Product',
    skills: ['Analytics', 'Figma', 'Growth'],
    tone: 'violet',
    signals: ['Open to hybrid', 'Available now'],
    placeholder: true,
  },
  {
    id: 'cand-rahul',
    name: 'Rahul',
    track: 'Software',
    skills: ['React', 'Node.js', 'PostgreSQL'],
    tone: 'teal',
    signals: ['Open to onsite', 'Available from Sep'],
    placeholder: true,
  },
];

/* ---------------- Metrics ----------------
   PLACEHOLDER METRICS. Entries flagged `source: 'live'` are overwritten at
   runtime with real counts from GET /api/stats; the rest are directional
   placeholders and the UI prints METRICS_FOOTNOTE while any remain.
   To ship a real number: set its value and drop `placeholder`. */
export const PLACEHOLDER_METRICS = [
  { id: 'students', value: 1000, suffix: '+', label: 'Students', note: 'On the platform', source: 'live', placeholder: true },
  { id: 'opportunities', value: 100, suffix: '+', label: 'Opportunities', note: 'Internships and early-career roles', placeholder: true },
  { id: 'startups', value: 50, suffix: '+', label: 'Startups', note: 'Hiring on HyrSkill', source: 'live', placeholder: true },
  { id: 'speed', value: 3, suffix: ' days', label: 'Faster hiring', note: 'From shortlist to first call', placeholder: true },
];

export const METRICS_FOOTNOTE =
  'Placeholder figures shown for layout. Replace with verified platform data before launch.';

/* ---------------- Final CTA ---------------- */
export const FINAL_CTA = {
  heading: 'The right opportunity can change everything.',
  sub: 'Whether you’re looking for your next internship or your next great hire, start with HyrSkill.',
  primary: { label: 'Find Opportunities', to: ROUTES.studentSignup },
  secondary: { label: 'Hire Talent', to: ROUTES.recruiterSignup },
};

/* ---------------- Footer ---------------- */
export const FOOTER = {
  blurb: 'HyrSkill is where great students meet great opportunities: internships and early-career roles at startups and companies.',
  columns: [
    {
      title: 'Students',
      links: [
        { label: 'Explore opportunities', to: ROUTES.studentSignup },
        { label: 'Create a profile', to: ROUTES.studentSignup },
        { label: 'Log in', to: ROUTES.login },
      ],
    },
    {
      title: 'Companies',
      links: [
        { label: 'Hire talent', to: ROUTES.recruiterSignup },
        { label: 'Post a role', to: ROUTES.recruiterSignup },
        { label: 'Find students', to: ROUTES.recruiterSignup },
        { label: 'Pricing', to: ROUTES.pricing },
      ],
    },
    {
      title: 'Platform',
      links: [
        { label: 'About', href: '#about' },
        { label: 'How it works', href: '#how' },
        { label: 'Opportunities', href: '#opportunities' },
      ],
    },
  ],
};
